import React, { useState, useRef, useEffect } from 'react';
import { useAuth } from '../Auth/AuthContext';
import DashboardStats from './DashboardStats';
import { apiCall } from '../../utils/apiUtils';

const DATE_OPTIONS = [
  { value: '7days', label: 'Last 7 Days' },
  { value: '30days', label: 'Last 30 Days' },
  { value: '6months', label: 'Last 6 Months' },
  { value: '1year', label: 'Past 1 Year' },
  { value: 'all', label: 'All Time' },
];

const ServiceCard = ({ card, onClick }) => (
  <button
    onClick={onClick}
    className={`text-left p-4 rounded-xl border ${card.color.border} ${card.color.bg} ${card.color.hover} transition-all duration-200 hover:shadow-md group`}
  >
    <div className="flex items-start justify-between mb-3">
      <span className={card.color.icon}>{card.icon}</span>
      {card.badge && (
        <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${card.color.badge}`}>
          {card.badge}
        </span>
      )}
    </div>
    <p className="font-semibold text-gray-900 text-sm">{card.title}</p>
    <p className="text-xs text-gray-500 mt-0.5 leading-relaxed">{card.description}</p>
    {card.metric && (
      <p className="text-xs font-medium text-gray-600 mt-2">{card.metric}</p>
    )}
    <div className="flex items-center mt-3 text-xs font-medium text-gray-400 group-hover:text-gray-600 transition-colors">
      Open
      <svg className="w-3 h-3 ml-1 group-hover:translate-x-0.5 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
      </svg>
    </div>
  </button>
);

const AdminToolButton = ({ tool, onClick }) => (
  <button
    onClick={onClick}
    className="flex items-center gap-2 p-3 rounded-xl bg-white border border-gray-200 hover:border-gray-300 hover:shadow-sm transition-all duration-200 text-left group"
  >
    <span className={`p-1.5 rounded-lg ${tool.color}`}>{tool.icon}</span>
    <span className="text-xs font-medium text-gray-700 group-hover:text-gray-900 truncate">{tool.title}</span>
  </button>
);

const PlatformHome = ({ stats, dateRange, setDateRange, setActiveTab, unreadNotificationCount }) => {
  const { user } = useAuth();
  const [analyticsOpen, setAnalyticsOpen] = useState(false);
  const [dateDropdownOpen, setDateDropdownOpen] = useState(false);
  const dateDropdownRef = useRef(null);
  const [recentNotifications, setRecentNotifications] = useState([]);
  const [notificationsLoading, setNotificationsLoading] = useState(true);

  const isAdmin = user?.role === 'admin';
  const isClient = user?.role === 'client';
  const isVendor = user?.role === 'vendor';
  const isAccountant = user?.role === 'accountant';
  const isExempt = isAdmin || isVendor || isAccountant;

  const aadhaarEnabled = isExempt || user?.api_services?.esign?.enabled;
  const digisignEnabled = isExempt || user?.api_services?.digisign?.enabled;
  const doqsolvEnabled = isExempt || user?.api_services?.doqsolv?.enabled;
  const workflowsEnabled = (isAdmin || isAccountant) || user?.api_services?.workflows?.enabled;
  const showWorkflows = process.env.REACT_APP_ENABLE_WORKFLOWS === 'true';

  useEffect(() => {
    const handler = (e) => {
      if (dateDropdownRef.current && !dateDropdownRef.current.contains(e.target)) {
        setDateDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  useEffect(() => {
    let isMounted = true;
    const fetchRecent = async () => {
      try {
        const response = await apiCall('/api/notifications?page=1&limit=5');
        if (isMounted) setRecentNotifications(response.notifications || []);
      } catch {
        // silently fail — not critical
      } finally {
        if (isMounted) setNotificationsLoading(false);
      }
    };
    fetchRecent();
    return () => { isMounted = false; };
  }, []);

  const serviceCards = [
    {
      id: 'orders-bulk',
      title: 'Bulk Stamping',
      description: 'Place and track bulk stamp paper orders',
      tab: 'orders-bulk',
      color: {
        bg: 'bg-blue-50', icon: 'text-blue-600', border: 'border-blue-200',
        hover: 'hover:border-blue-400', badge: 'bg-blue-100 text-blue-700',
      },
      icon: (
        <svg className="w-7 h-7" fill="currentColor" viewBox="0 0 24 24">
          <path d="M19 3H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2zm-5 14H7v-2h7v2zm3-4H7v-2h10v2zm0-4H7V7h10v2z" />
        </svg>
      ),
      metric: stats.total_orders ? `${(stats.total_orders || 0).toLocaleString()} total` : null,
      badge: (stats.pending_orders > 0) ? `${stats.pending_orders} pending` : null,
      show: true,
    },
    {
      id: 'orders-live',
      title: 'Live Stamping',
      description: 'Real-time stamp processing and vendor uploads',
      tab: 'orders-live',
      color: {
        bg: 'bg-green-50', icon: 'text-green-600', border: 'border-green-200',
        hover: 'hover:border-green-400', badge: 'bg-green-100 text-green-700',
      },
      icon: (
        <svg className="w-7 h-7" fill="currentColor" viewBox="0 0 24 24">
          <path d="M11 21h-1l1-7H7.5a.5.5 0 01-.4-.8L13 3h1l-1 7h3.5a.5.5 0 01.4.8L11 21z" />
        </svg>
      ),
      metric: null,
      badge: null,
      show: !isAccountant,
    },
    {
      id: 'esign-aadhaar',
      title: 'Aadhaar eSign',
      description: 'Aadhaar-based electronic signatures',
      tab: 'esign-aadhaar',
      color: {
        bg: 'bg-emerald-50', icon: 'text-emerald-600', border: 'border-emerald-200',
        hover: 'hover:border-emerald-400', badge: 'bg-emerald-100 text-emerald-700',
      },
      icon: (
        <svg className="w-7 h-7" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
        </svg>
      ),
      metric: null,
      badge: 'Aadhaar',
      show: (isAdmin || isClient) && aadhaarEnabled,
    },
    {
      id: 'esign-digital',
      title: 'Digital Signature',
      description: 'DSC-based document signing',
      tab: 'esign-digital',
      color: {
        bg: 'bg-violet-50', icon: 'text-violet-600', border: 'border-violet-200',
        hover: 'hover:border-violet-400', badge: 'bg-violet-100 text-violet-700',
      },
      icon: (
        <svg className="w-7 h-7" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
        </svg>
      ),
      metric: null,
      badge: 'DSC',
      show: (isAdmin || isClient) && digisignEnabled,
    },
    {
      id: 'doqsolv',
      title: 'DoqSolv',
      description: 'Intelligent document processing',
      tab: 'doqsolv',
      color: {
        bg: 'bg-indigo-50', icon: 'text-indigo-600', border: 'border-indigo-200',
        hover: 'hover:border-indigo-400', badge: 'bg-indigo-100 text-indigo-700',
      },
      icon: (
        <svg className="w-7 h-7" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
        </svg>
      ),
      metric: null,
      badge: null,
      show: (isAdmin || isClient) && doqsolvEnabled,
    },
    {
      id: 'workflow-builder',
      title: 'Workflows',
      description: 'Automate document processing pipelines',
      tab: 'workflow-builder',
      color: {
        bg: 'bg-orange-50', icon: 'text-orange-600', border: 'border-orange-200',
        hover: 'hover:border-orange-400', badge: 'bg-orange-100 text-orange-700',
      },
      icon: (
        <svg className="w-7 h-7" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 5a1 1 0 011-1h14a1 1 0 011 1v2a1 1 0 01-1 1H5a1 1 0 01-1-1V5zM4 13a1 1 0 011-1h6a1 1 0 011 1v6a1 1 0 01-1 1H5a1 1 0 01-1-1v-6zM16 13a1 1 0 011-1h2a1 1 0 011 1v6a1 1 0 01-1 1h-2a1 1 0 01-1-1v-6z" />
        </svg>
      ),
      metric: null,
      badge: null,
      show: (isAdmin || isClient) && workflowsEnabled && showWorkflows,
    },
    {
      id: 'invoices',
      title: 'Invoices',
      description: 'Billing and payment management',
      tab: 'invoices',
      color: {
        bg: 'bg-pink-50', icon: 'text-pink-600', border: 'border-pink-200',
        hover: 'hover:border-pink-400', badge: 'bg-pink-100 text-pink-700',
      },
      icon: (
        <svg className="w-7 h-7" fill="currentColor" viewBox="0 0 24 24">
          <path d="M14,2H6A2,2 0 0,0 4,4V20A2,2 0 0,0 6,22H18A2,2 0 0,0 20,20V8L14,2M18,20H6V4H13V9H18V20Z" />
        </svg>
      ),
      metric: null,
      badge: null,
      show: isAdmin || isAccountant,
    },
  ].filter(c => c.show);

  const adminTools = isAdmin ? [
    {
      id: 'users', title: 'Users', tab: 'users',
      color: 'text-purple-600 bg-purple-50',
      icon: (
        <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
          <path d="M16 7c0-2.21-1.79-4-4-4S8 4.79 8 7s1.79 4 4 4 4-1.79 4-4zm-4 6c-2.67 0-8 1.34-8 4v3h16v-3c0-2.66-5.33-4-8-4z" />
        </svg>
      ),
    },
    {
      id: 'api-services', title: 'API Services', tab: 'api-services',
      color: 'text-sky-600 bg-sky-50',
      icon: (
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 9l3 3-3 3m5 0h3M5 20h14a2 2 0 002-2V6a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
        </svg>
      ),
    },
    {
      id: 'client-webhook', title: 'Webhooks', tab: 'client-webhook',
      color: 'text-teal-600 bg-teal-50',
      icon: (
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.111 16.404a5.5 5.5 0 017.778 0M12 20h.01m-7.08-7.071c3.904-3.905 10.236-3.905 14.14 0M1.394 9.393c5.857-5.858 15.355-5.858 21.213 0" />
        </svg>
      ),
    },
    {
      id: 'notification-settings', title: 'Email Settings', tab: 'notification-settings',
      color: 'text-amber-600 bg-amber-50',
      icon: (
        <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
          <path d="M20 4H4c-1.1 0-1.99.9-1.99 2L2 18c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V6c0-1.1-.9-2-2-2zm0 4l-8 5-8-5V6l8 5 8-5v2z" />
        </svg>
      ),
    },
    {
      id: 'storage-cleanup', title: 'Storage', tab: 'storage-cleanup',
      color: 'text-red-600 bg-red-50',
      icon: (
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 7v10c0 2.21 3.582 4 8 4s8-1.79 8-4V7M4 7c0 2.21 3.582 4 8 4s8-1.79 8-4M4 7c0-2.21 3.582-4 8-4s8 1.79 8 4m0 5c0 2.21-3.582 4-8 4s-8-1.79-8-4" />
        </svg>
      ),
    },
  ] : [];

  const serviceGridCols = serviceCards.length <= 2
    ? 'grid-cols-2'
    : serviceCards.length <= 4
      ? 'grid-cols-2 md:grid-cols-4'
      : 'grid-cols-2 md:grid-cols-3';

  return (
    <div className="space-y-3">
      {/* Services grid */}
      <div>
        <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider px-1 mb-2">Services</p>
        <div className={`grid ${serviceGridCols} gap-3`}>
          {serviceCards.map(card => (
            <ServiceCard key={card.id} card={card} onClick={() => setActiveTab(card.tab)} />
          ))}
        </div>
      </div>

      {/* Administration row (admin only) */}
      {adminTools.length > 0 && (
        <div>
          <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider px-1 mb-2">Administration</p>
          <div className="grid grid-cols-3 md:grid-cols-5 gap-2">
            {adminTools.map(tool => (
              <AdminToolButton key={tool.id} tool={tool} onClick={() => setActiveTab(tool.tab)} />
            ))}
          </div>
        </div>
      )}

      {/* Notifications + Quick Actions — symmetric two-column row */}
      <div className="grid grid-cols-2 gap-3">

        {/* Left: Recent Notifications */}
        <div className="bg-white rounded-lg border border-gray-100 shadow-sm overflow-hidden flex flex-col">
          <div className="flex items-center justify-between px-5 py-3 border-b border-gray-50">
            <div className="flex items-center gap-2">
              <svg className="w-4 h-4 text-gray-400" fill="currentColor" viewBox="0 0 24 24">
                <path d="M12 22c1.1 0 2-.9 2-2h-4c0 1.1.9 2 2 2zm6-6v-5c0-3.07-1.64-5.64-4.5-6.32V4c0-.83-.67-1.5-1.5-1.5s-1.5.67-1.5 1.5v.68C7.63 5.36 6 7.92 6 11v5l-2 2v1h16v-1l-2-2z" />
              </svg>
              <span className="text-sm font-semibold text-gray-700">Recent Notifications</span>
              {unreadNotificationCount > 0 && (
                <span className="text-xs font-bold bg-red-500 text-white px-1.5 py-0.5 rounded-full">
                  {unreadNotificationCount > 99 ? '99+' : unreadNotificationCount}
                </span>
              )}
            </div>
            <button
              onClick={() => setActiveTab('notifications')}
              className="text-xs font-medium text-blue-600 hover:text-blue-800 transition-colors flex items-center gap-1"
            >
              View all
              <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
              </svg>
            </button>
          </div>

          {notificationsLoading ? (
            <div className="flex-1 flex items-center justify-center py-8">
              <div className="w-5 h-5 border-2 border-blue-200 border-t-blue-500 rounded-full animate-spin" />
            </div>
          ) : recentNotifications.length === 0 ? (
            <div className="flex-1 flex items-center justify-center py-8 text-sm text-gray-400">No notifications yet</div>
          ) : (
            <div className="divide-y divide-gray-50">
              {recentNotifications.map(n => {
                const typeStyles = {
                  success: { dot: 'bg-green-500', bg: '' },
                  warning: { dot: 'bg-amber-500', bg: '' },
                  error:   { dot: 'bg-red-500',   bg: 'bg-red-50' },
                  info:    { dot: 'bg-blue-500',  bg: '' },
                };
                const style = typeStyles[n.type] || typeStyles.info;
                return (
                  <div
                    key={n.id}
                    className={`flex items-start gap-3 px-5 py-3 hover:bg-gray-50 transition-colors ${!n.read ? style.bg : ''}`}
                  >
                    <span className={`mt-1.5 w-2 h-2 rounded-full flex-shrink-0 ${style.dot} ${n.read ? 'opacity-30' : ''}`} />
                    <div className="min-w-0 flex-1">
                      <p className={`text-sm truncate ${n.read ? 'text-gray-500 font-normal' : 'text-gray-900 font-medium'}`}>
                        {n.title}
                      </p>
                      {n.message && (
                        <p className="text-xs text-gray-400 truncate mt-0.5">{n.message}</p>
                      )}
                    </div>
                    <span className="text-xs text-gray-300 flex-shrink-0 mt-0.5">
                      {new Date(n.created_at).toLocaleDateString('en-IN', { day: 'numeric', month: 'short' })}
                    </span>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Right: Quick Actions — only items NOT already shown as service cards or admin tiles */}
        {(() => {
          const actions = [
            {
              label: isVendor ? 'Upload Stamped Files' : 'Bulk Orders Import / Export',
              tab: 'bulk-import-export',
              hover: 'hover:bg-amber-50 hover:border-amber-200',
              iconColor: 'text-amber-600',
              labelHover: 'group-hover:text-amber-700',
              show: !isAccountant,
              icon: <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12" />,
            },
            {
              label: 'API Services',
              tab: 'api-services',
              hover: 'hover:bg-sky-50 hover:border-sky-200',
              iconColor: 'text-sky-600',
              labelHover: 'group-hover:text-sky-700',
              show: isAdmin || isClient,
              icon: <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 9l3 3-3 3m5 0h3M5 20h14a2 2 0 002-2V6a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />,
            },
            {
              label: 'Settings',
              tab: 'settings',
              hover: 'hover:bg-slate-100 hover:border-slate-300',
              iconColor: 'text-slate-600',
              labelHover: 'group-hover:text-slate-900',
              show: true,
              icon: <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065zM15 12a3 3 0 11-6 0 3 3 0 016 0z" />,
            },
          ].filter(a => a.show);

          return (
            <div className="bg-white rounded-lg border border-gray-100 shadow-sm overflow-hidden flex flex-col">
              <div className="px-5 py-3 border-b border-gray-50">
                <span className="text-sm font-semibold text-gray-700">Quick Actions</span>
              </div>
              <div className="flex-1 p-4">
                <div className="grid grid-cols-2 gap-2">
                  {actions.map(a => (
                    <button
                      key={a.tab + a.label}
                      onClick={() => setActiveTab(a.tab)}
                      className={`flex flex-col items-center justify-center gap-2 p-3 bg-gray-50 border border-gray-200 ${a.hover} rounded-xl transition-all duration-200 group`}
                    >
                      <span className="bg-white p-2 rounded-lg shadow-sm group-hover:scale-110 transition-transform">
                        <svg className={`w-5 h-5 ${a.iconColor}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          {a.icon}
                        </svg>
                      </span>
                      <span className={`text-xs font-medium text-gray-700 ${a.labelHover} text-center leading-tight`}>{a.label}</span>
                    </button>
                  ))}
                </div>
              </div>
            </div>
          );
        })()}

      </div>

      {/* Collapsible Bulk Stamping Analytics */}
      <div className="bg-white rounded-lg border border-gray-100 shadow-sm overflow-hidden">
        <button
          onClick={() => setAnalyticsOpen(prev => !prev)}
          className="w-full flex items-center justify-between px-6 py-3 hover:bg-gray-50 transition-colors"
        >
          <div className="flex items-center gap-2">
            <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
            </svg>
            <span className="text-sm font-semibold text-gray-700">Bulk Stamping Analytics</span>
          </div>
          <div className="flex items-center gap-3">
            {analyticsOpen && (
              <div className="relative" ref={dateDropdownRef}>
                <button
                  type="button"
                  onClick={(e) => { e.stopPropagation(); setDateDropdownOpen(prev => !prev); }}
                  className="flex items-center gap-2 bg-white border border-gray-200 hover:border-gray-300 rounded-lg px-3 py-1.5 text-xs font-medium text-gray-700 focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all shadow-sm"
                >
                  <svg className="w-3.5 h-3.5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                  </svg>
                  <span>{DATE_OPTIONS.find(o => o.value === dateRange)?.label}</span>
                  <svg className={`w-3 h-3 text-gray-400 transition-transform ${dateDropdownOpen ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                </button>
                {dateDropdownOpen && (
                  <div className="absolute right-0 mt-1 w-44 bg-white rounded-lg border border-gray-100 shadow-lg z-50 overflow-hidden">
                    <div className="py-1">
                      {DATE_OPTIONS.map(opt => (
                        <button
                          key={opt.value}
                          onClick={(e) => { e.stopPropagation(); setDateRange(opt.value); setDateDropdownOpen(false); }}
                          className={`w-full text-left px-4 py-2 text-xs transition-colors flex items-center justify-between ${
                            dateRange === opt.value
                              ? 'bg-blue-50 text-blue-700 font-medium'
                              : 'text-gray-700 hover:bg-gray-50'
                          }`}
                        >
                          {opt.label}
                          {dateRange === opt.value && (
                            <svg className="w-3.5 h-3.5 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                            </svg>
                          )}
                        </button>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}
            <svg
              className={`w-4 h-4 text-gray-400 transition-transform duration-200 ${analyticsOpen ? 'rotate-180' : ''}`}
              fill="none" stroke="currentColor" viewBox="0 0 24 24"
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
            </svg>
          </div>
        </button>

        {analyticsOpen && (
          <div className="px-2 pb-2">
            <DashboardStats
              stats={stats}
              dateRange={dateRange}
              setActiveTab={setActiveTab}
              unreadNotificationCount={unreadNotificationCount}
            />
          </div>
        )}
      </div>
    </div>
  );
};

export default PlatformHome;
